import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Save, Scale } from 'lucide-react';
import { getApiBase, getAuthHeaders } from '../../utils/api';

const PROVIDER_LABELS = { flightaware: 'FlightAware', aerodatabox: 'AeroDataBox' };

const FIELD_LABELS = {
  flight_status: 'Flight status',
  departure_terminal: 'Departure terminal', departure_gate: 'Departure gate', departure_checkin: 'Check-in desk',
  arrival_terminal: 'Arrival terminal', arrival_gate: 'Arrival gate', arrival_baggage_claim: 'Baggage claim',
  aircraft_type: 'Aircraft type', aircraft_registration: 'Tail number',
  scheduled_departure: 'Scheduled departure', estimated_departure: 'Estimated departure',
  actual_departure: 'Actual departure', predicted_departure: 'Predicted departure',
  scheduled_arrival: 'Scheduled arrival', estimated_arrival: 'Estimated arrival',
  actual_arrival: 'Actual arrival', predicted_arrival: 'Predicted arrival',
  runway_departure: 'Wheels-off', runway_arrival: 'Wheels-on',
  delay_departure_minutes: 'Departure delay', delay_arrival_minutes: 'Arrival delay',
};

const percent = (value) => (value == null ? '—' : `${Math.round(value * 100)}%`);

/**
 * Superadmin control for which provider wins each flight-leg field.
 * FlightAware wins every field unless changed here; the winner's value is
 * never replaced by the other provider. The 30-day comparison scorecard is
 * shown beside each field so the choice is based on measured agreement.
 */
const FlightDataPrecedencePanel = () => {
  const [config, setConfig] = useState(null);
  const [draft, setDraft] = useState({});
  const [scorecard, setScorecard] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const headers = getAuthHeaders();
    axios.get(`${getApiBase()}/admin/flight-data/precedence`, { headers })
      .then(({ data }) => {
        if (!Array.isArray(data?.fields)) { setMessage('Could not load provider precedence.'); return; }
        setConfig(data); setDraft(data.precedence || {});
      })
      .catch(() => setMessage('Could not load provider precedence.'));
    axios.get(`${getApiBase()}/admin/aerodatabox/comparison`, { headers, params: { days: 30 } })
      .then(({ data }) => setScorecard(data))
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true); setMessage('');
    try {
      const { data } = await axios.put(`${getApiBase()}/admin/flight-data/precedence`, { fields: draft }, { headers: getAuthHeaders() });
      setConfig(prev => ({ ...prev, precedence: data.precedence }));
      setDraft(data.precedence || {});
      setMessage('Saved. New provider updates follow these rules within a minute.');
    } catch (error) {
      setMessage('Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const cardClass = 'border-ui-line bg-ui-surface text-ui-ink';
  const mutedText = 'text-ui-ink-3';
  const rowBorder = 'border-ui-divider';
  const dirty = config && JSON.stringify(draft) !== JSON.stringify(config.precedence || {});

  return (
    <section className={`rounded-2xl border p-5 shadow-ui-card ${cardClass}`} data-testid="flight-data-precedence">
      <div className="flex items-center gap-2">
        <Scale className="h-5 w-5 text-ui-link" />
        <h2 className="text-lg font-semibold">Flight data provider precedence</h2>
      </div>
      <p className={`mt-1 text-sm ${mutedText}`}>
        Choose which provider wins each field. The winner’s value is never replaced by the other provider; the other provider only fills the field while it is empty.
        Agreement is measured over the last 30 days of comparisons{scorecard ? ` (${scorecard.records || 0} records)` : ''}.
      </p>
      {!config ? (
        <div className={`mt-4 text-sm ${mutedText}`}>{message || <Loader2 className="h-4 w-4 animate-spin" />}</div>
      ) : (
        <>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className={mutedText}>
                <tr>
                  <th className="py-2 pr-4 font-medium">Field</th>
                  <th className="py-2 pr-4 font-medium">Winner</th>
                  <th className="py-2 pr-4 font-medium">Both have it</th>
                  <th className="py-2 pr-4 font-medium">Only FlightAware</th>
                  <th className="py-2 pr-4 font-medium">Only AeroDataBox</th>
                  <th className="py-2 font-medium">Agreement</th>
                </tr>
              </thead>
              <tbody>
                {config.fields.map(field => {
                  const stats = scorecard?.fields?.[field] || {};
                  return (
                    <tr key={field} className={`border-t ${rowBorder}`}>
                      <td className="py-2 pr-4">{FIELD_LABELS[field] || field}</td>
                      <td className="py-2 pr-4">
                        <select
                          aria-label={`Winner for ${FIELD_LABELS[field] || field}`}
                          value={draft[field] || config.default_provider}
                          onChange={(event) => setDraft(prev => ({ ...prev, [field]: event.target.value }))}
                          className="rounded-md border border-ui-control bg-ui-sunk px-2 py-1 text-sm text-ui-ink"
                        >
                          {config.providers.map(provider => <option key={provider} value={provider}>{PROVIDER_LABELS[provider] || provider}</option>)}
                        </select>
                      </td>
                      <td className="py-2 pr-4">{stats.both ?? '—'}</td>
                      <td className="py-2 pr-4">{stats.flightaware_only ?? '—'}</td>
                      <td className="py-2 pr-4">{stats.aerodatabox_only ?? '—'}</td>
                      <td className="py-2">{percent(stats.agreement_rate)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={save}
              disabled={!dirty || saving}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-ui-primary px-4 text-sm font-semibold text-ui-on-primary hover:bg-ui-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save precedence
            </button>
            {message && <span className={`text-sm ${mutedText}`}>{message}</span>}
            {config.updated_at && <span className={`text-xs ${mutedText}`}>Last changed {new Date(config.updated_at).toLocaleString()}{config.updated_by ? ` by ${config.updated_by}` : ''}</span>}
          </div>
        </>
      )}
    </section>
  );
};

export default FlightDataPrecedencePanel;
